import {
  Box,
  Button,
  Container,
  Dialog,
  Grid,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import { TableCellType } from "components/LazyTable";
import SnackbarMessage from "components/SnackbarMessage";
import SortableTable from "components/SortableTable";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useTrashablePromises from "hooks/useTrashablePromises";
import React, { useCallback, useMemo, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import {
  CertificateProductExtended,
  ICertificateProductRequest,
  ListRequestOfCertificateProductFilter,
  ListResponseOfCertificateProductExtended,
} from "services/KnowfullyAdminClient";
import { Severity, SnackbarMessageProps } from "types/common-types";
import translateError from "utils/translateError";
import CertificateProductForm from "./CertificateProductForm";

type CertificateProductsFormProps = {
  certificateId: string;
};

interface CertificateProductExtendedWithId extends CertificateProductExtended {
  id: string;
}

const CertificateProductsList = ({
  certificateId,
}: CertificateProductsFormProps) => {
  const { registerPromise } = useTrashablePromises();
  const { formatMessage: t } = useIntl();
  const queryClient = useQueryClient();
  const { certificateService } = useGlobalContext();
  const [deleting, setDeleting] = useState<CertificateProductExtended | null>(
    null
  );
  const [editing, setEditing] = useState<CertificateProductExtended | null>(
    null
  );
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const [createOpen, createOpenActions] = useBoolean(false);
  const [deleteOpen, deleteOpenActions] = useBoolean(false);

  const {
    data: certificateProductsData,
    isFetching: isFetchingCertificateProducts,
  } = useQuery<ListResponseOfCertificateProductExtended>(
    ["certificateProducts", certificateId],
    () =>
      certificateService.getCertificateProducts(
        certificateId,
        ListRequestOfCertificateProductFilter.fromJS({
          sortField: "createdOn",
          sortDescending: false,
        })
      ),
    {
      enabled: !!certificateId,
    }
  );

  const handleDelete = useCallback(async () => {
    const id = deleting?.id;
    setDeleting(null);
    if (id) {
      try {
        await registerPromise(certificateService.deleteCertificateProduct(id));
        queryClient.invalidateQueries(["certificateProducts", certificateId]);

        setSnackbarMessage(
          new SnackbarMessageProps(
            t({ id: "certificateProductDeleted" }),
            Severity.Warning
          )
        );

        deleteOpenActions.setFalse();
      } catch (err: any) {
        return { [FORM_ERROR]: translateError(err, t) };
      }
    }
  }, [
    certificateId,
    certificateService,
    deleteOpenActions,
    deleting?.id,
    queryClient,
    registerPromise,
    t,
  ]);

  const DeleteButton = ({ value }: { value: CertificateProductExtended }) => {
    return (
      <Box textAlign="center">
        <IconButton
          size="small"
          onClick={() => {
            setDeleting(value);
            deleteOpenActions.setTrue();
          }}
        >
          <DeleteIcon />
        </IconButton>
      </Box>
    );
  };

  const EditButton = ({ value }: { value: CertificateProductExtended }) => {
    return (
      <Box textAlign="center">
        <IconButton
          size="small"
          onClick={() => {
            setEditing(value);
            createOpenActions.setTrue();
          }}
        >
          <EditIcon />
        </IconButton>
      </Box>
    );
  };

  const certificateProducts = useMemo(
    () => getProducts(certificateProductsData),
    [certificateProductsData]
  );

  function getProducts(
    certificateProductsData:
      | ListResponseOfCertificateProductExtended
      | undefined
  ) {
    certificateProductsData?.data?.forEach((x) => {
      x.product = x.product ?? "-";
      x.productType = x.productType ?? "-";
      x.skuAvailability = x.skuAvailability ?? "-";
      x.productVersion = x.productVersion ?? "-";
      x.productYear = x.productYear ?? "-";
    });
    return (certificateProductsData?.data ??
      []) as CertificateProductExtendedWithId[];
  }

  const cells: Array<TableCellType> = [
    {
      accessor: "product",
      align: "left",
      type: "text",
      label: t({ id: "certificateProductProductLabel" }),
    },
    {
      accessor: "productYear",
      align: "left",
      type: "text",
      label: t({ id: "certificateProductProductYearLabel" }),
    },
    {
      accessor: "skuAvailability",
      align: "left",
      type: "text",
      label: t({ id: "skuAvailabilityLabel" }),
    },
    {
      accessor: "productVersion",
      align: "left",
      type: "text",
      label: t({ id: "certificateProductProductVersionLabel" }),
    },
    {
      accessor: "productType",
      align: "left",
      type: "text",
      label: t({ id: "certificateProductProductTypeLabel" }),
    },
    {
      accessor: "value",
      align: "center",
      width: 1,
      type: "node",
      View: EditButton,
      label: "",
      noSort: true,
    },
    {
      accessor: "value",
      align: "center",
      width: 1,
      type: "node",
      View: DeleteButton,
      label: "",
      noSort: true,
    },
  ];

  const onSubmit = async (values: ICertificateProductRequest) => {
    setSnackbarMessage(
      new SnackbarMessageProps(t({ id: "certificateProductCreateMessage" }))
    );

    queryClient.invalidateQueries(["certificateProducts", certificateId]);
    createOpenActions.setFalse();
  };

  return (
    <Container>
      <Box mb={3}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Box textAlign="right">
              <Button
                variant="outlined"
                type="submit"
                color="primary"
                onClick={() => {
                  setEditing(null);
                  createOpenActions.setTrue();
                }}
                data-cy="addCertificateProductButton"
              >
                <T id="certificateProductCreateButtonLabel" />
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <SortableTable
        {...{
          rows: certificateProducts,
          loading: isFetchingCertificateProducts,
          dense: true,
          cells,
        }}
      />

      <DeleteEntityDialog
        dialogTitleKey={"certificateProductDeleteTitle"}
        contentKey={"certificateProductDeleteQuestion"}
        onCancel={deleteOpenActions.setFalse}
        handleDelete={handleDelete}
        open={deleteOpen}
      />
      <Dialog open={createOpen} disableEnforceFocus maxWidth="sm" fullWidth>
        <CertificateProductForm
          certificateId={certificateId}
          certificateProductId={editing?.id}
          entity={editing}
          onCancel={createOpenActions.setFalse}
          onSubmit={onSubmit}
        />
      </Dialog>
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Container>
  );
};

export default CertificateProductsList;
